import html2pdf from 'html2pdf.js';
import jsPDF from 'jspdf';

const PAGE_WIDTH_MM = 210;
const PAGE_HEIGHT_MM = 297;

/**
 * Builds the tear sheet PDF from a rendered <TearSheetPdfDocument>.
 *
 * Every sheet is rasterised on its own canvas. Handing the whole document to
 * html2pdf in one go draws every sheet onto a single canvas, and once that
 * canvas passes the browser's size limit (~29 items in Chrome / Edge, fewer in
 * Safari and Firefox, a handful on an iPad) the browser silently hands back an
 * empty canvas — the PDF still has one page per item, but every page is blank.
 *
 * @param {HTMLElement} rootEl  Element wrapping the rendered tear sheet document.
 * @returns {Promise<jsPDF>}
 */
export async function buildTearSheetPdf(rootEl) {
  const shell = rootEl.querySelector('.ts-pdf');
  const sheets = Array.from(rootEl.querySelectorAll('.ts-pdf__page'));
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  let pageCount = 0;

  for (const sheet of sheets) {
    // Wrap the sheet in a bare copy of the .ts-pdf root so it keeps the root's
    // font and colour once html2pdf moves it into its own capture container.
    const wrapper = shell.cloneNode(false);
    wrapper.appendChild(sheet.cloneNode(true));

    let canvas;
    try {
      canvas = await html2pdf()
        .set({
          margin: 0,
          html2canvas: {
            scale: 2,
            useCORS: true,
            backgroundColor: '#fff',
            // Skip re-cloning the whole off-screen set on every capture.
            ignoreElements: (node) => node === rootEl,
          },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: [] },
          enableLinks: false,
        })
        .from(wrapper)
        .toCanvas()
        .get('canvas');
    } catch (err) {
      // html2pdf leaves its (invisible, full-screen) overlay behind on failure,
      // which would swallow every click on the page.
      document.querySelectorAll('.html2pdf__overlay').forEach((el) => el.remove());
      throw err;
    }

    const firstPage = pageCount + 1;
    pageCount += addCanvasPages(pdf, canvas, pageCount === 0);
    addLinks(pdf, sheet, firstPage, pageCount);
  }

  return pdf;
}

// Adds the canvas to the PDF, spilling onto extra pages if a sheet's content
// runs longer than one A4 page. Returns the number of pages added.
function addCanvasPages(pdf, canvas, startsOnFirstPage) {
  const pxPerMm = canvas.width / PAGE_WIDTH_MM;
  const pxPageHeight = Math.floor(PAGE_HEIGHT_MM * pxPerMm);
  const slice = document.createElement('canvas');
  const ctx = slice.getContext('2d');
  slice.width = canvas.width;

  let added = 0;
  for (let y = 0; y < canvas.height; y += pxPageHeight) {
    const h = Math.min(pxPageHeight, canvas.height - y);
    slice.height = h;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, slice.width, h);
    ctx.drawImage(canvas, 0, y, slice.width, h, 0, 0, slice.width, h);

    if (!startsOnFirstPage || added > 0) pdf.addPage();
    pdf.addImage(slice.toDataURL('image/jpeg', 0.98), 'JPEG', 0, 0, PAGE_WIDTH_MM, h / pxPerMm);
    added += 1;
  }
  return added;
}

// Keeps the sheet's links (e.g. "View Product") clickable in the PDF.
function addLinks(pdf, sheet, firstPage, lastPage) {
  const sheetRect = sheet.getBoundingClientRect();
  if (!sheetRect.width) return;
  const mmPerPx = PAGE_WIDTH_MM / sheetRect.width;

  sheet.querySelectorAll('a[href]').forEach((link) => {
    for (const rect of link.getClientRects()) {
      const top = (rect.top - sheetRect.top) * mmPerPx;
      const page = Math.min(firstPage + Math.floor(top / PAGE_HEIGHT_MM), lastPage);
      pdf.setPage(page);
      pdf.link(
        (rect.left - sheetRect.left) * mmPerPx,
        top % PAGE_HEIGHT_MM,
        rect.width * mmPerPx,
        rect.height * mmPerPx,
        { url: link.href }
      );
    }
  });
  pdf.setPage(lastPage);
}
